import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useStudentsMap from '../../../../hooks/useStudentsMap';
import { MOCK_STUDENTS_MAP } from '../../../../mocks/mockStudents';
import analyticsService from '../../../../services/analyticsService';
import type { GroupAffectedStudent, GroupLearningAlertDetail, RadarSeverity } from '../../../../types/radar';
import { formatLocations, getSeverityFromErrorRate } from '../../../../utils/radarUtils';
import { getMockGroupLearningAlertDetails } from '../../mockRadarData';

import type { StudentInfo } from '../../../../hooks/useStudentsMap';

type UseGroupLearningBlockContentProps = {
  annotationId: string;
  isMockMode?: boolean;
};

export type EnrichedAffectedStudent = GroupAffectedStudent & {
  avatar: string | null;
  correctCount: number;
  groupName: string;
};

type SeverityGroup = {
  severity: RadarSeverity;
  students: EnrichedAffectedStudent[];
};

type UseGroupLearningBlockContentReturn = {
  detail: GroupLearningAlertDetail | null;
  error: string | null;
  handleToggleStudents: () => void;
  isLoading: boolean;
  isStudentsExpanded: boolean;
  locationItems: string[];
  studentsBySeverity: SeverityGroup[];
  t: ReturnType<typeof useTranslation>['t'];
};

function getStudentCorrectCount(student: GroupAffectedStudent): number {
  return Math.max(0, Math.round(student.attemptCount * (1 - student.errorRate)));
}

function groupStudentsBySeverity(
  students: GroupAffectedStudent[],
  effectiveStudentsMap: Map<string, StudentInfo>,
): SeverityGroup[] {
  const high: EnrichedAffectedStudent[] = [];
  const medium: EnrichedAffectedStudent[] = [];
  const low: EnrichedAffectedStudent[] = [];

  students.forEach((student) => {
    const info = effectiveStudentsMap.get(student.studentExternalId);
    const enriched: EnrichedAffectedStudent = {
      ...student,
      avatar: info?.avatar ?? null,
      correctCount: getStudentCorrectCount(student),
      groupName: info?.groupName ?? '',
    };
    const severity = getSeverityFromErrorRate(student.errorRate);
    if (severity === 'high') {
      high.push(enriched);
    } else if (severity === 'medium') {
      medium.push(enriched);
    } else {
      low.push(enriched);
    }
  });

  const groups: SeverityGroup[] = [];
  if (high.length > 0) groups.push({ severity: 'high', students: high });
  if (medium.length > 0) groups.push({ severity: 'medium', students: medium });
  if (low.length > 0) groups.push({ severity: 'low', students: low });

  return groups;
}

function useGroupLearningBlockContent({ annotationId, isMockMode }: UseGroupLearningBlockContentProps): UseGroupLearningBlockContentReturn {
  const { i18n, t } = useTranslation(['chatbot']);
  const [detail, setDetail] = useState<GroupLearningAlertDetail | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isStudentsExpanded, setIsStudentsExpanded] = useState<boolean>(false);

  const { studentsMap } = useStudentsMap();
  const effectiveStudentsMap = isMockMode ? MOCK_STUDENTS_MAP : studentsMap;

  useEffect(() => {
    if (!annotationId) return;

    if (isMockMode) {
      const mockDetail = getMockGroupLearningAlertDetails(i18n.language)[annotationId] ?? null;
      setError(null);
      setDetail(mockDetail);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchDetail(): Promise<void> {
      setIsLoading(true);
      setError(null);

      try {
        const data = await analyticsService.getAnnotationDetail(annotationId);
        if (!cancelled) {
          setDetail(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Unknown error');
          setDetail(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchDetail();

    return () => {
      cancelled = true;
    };
  }, [annotationId, isMockMode, i18n.language]);

  const handleToggleStudents = useCallback(() => {
    setIsStudentsExpanded((prev) => !prev);
  }, []);

  const locationItems = useMemo(() => (detail ? formatLocations(detail.locations) : []), [detail]);
  const studentsBySeverity = useMemo(
    () => (detail ? groupStudentsBySeverity(detail.affectedStudents, effectiveStudentsMap) : []),
    [detail, effectiveStudentsMap],
  );

  return { detail, error, handleToggleStudents, isLoading, isStudentsExpanded, locationItems, studentsBySeverity, t };
}

export default useGroupLearningBlockContent;
